import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import '../styles/DatePicker.css';

function DatePicker({ 
  value, 
  onChange, 
  placeholder = 'Select date',
  disabled = false,
  minDate = null,
  name = '',
  required = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [displayMonth, setDisplayMonth] = useState(new Date());
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Parse the value (YYYY-MM-DD string) into a Date object
  const selectedDate = value ? new Date(value + 'T00:00:00') : null;

  // Set display month to selected date's month if available
  useEffect(() => {
    if (value) {
      const date = new Date(value + 'T00:00:00');
      if (!isNaN(date)) {
        setDisplayMonth(new Date(date.getFullYear(), date.getMonth(), 1));
      }
    }
  }, [value]);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const formatDisplayDate = (date) => {
    if (!date || isNaN(date)) return '';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  };

  const handleInputClick = () => {
    if (!disabled) {
      if (!isOpen && inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        const dropdownHeight = 380; // Approximate height of calendar dropdown
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        
        let top, left;
        left = rect.left;
        
        // If not enough space below and more space above, show above
        if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
          top = rect.top - dropdownHeight - 4;
        } else {
          top = rect.bottom + 4;
        }
        
        // Keep within viewport horizontally
        const dropdownWidth = 300;
        if (left + dropdownWidth > window.innerWidth) {
          left = window.innerWidth - dropdownWidth - 16;
        }
        
        setDropdownPosition({ top, left });
      }
      setIsOpen(!isOpen);
    }
  };

  const handleDateSelect = (date) => {
    // Format as YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    
    onChange({ target: { name, value: formattedDate } });
    setIsOpen(false);
  };

  const handlePrevMonth = () => {
    setDisplayMonth(new Date(displayMonth.getFullYear(), displayMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setDisplayMonth(new Date(displayMonth.getFullYear(), displayMonth.getMonth() + 1, 1));
  };

  const handleToday = () => {
    const today = new Date();
    handleDateSelect(today);
  };

  const handleClear = () => {
    onChange({ target: { name, value: '' } });
    setIsOpen(false);
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = displayMonth.getFullYear();
    const month = displayMonth.getMonth();
    
    // First day of the month
    const firstDay = new Date(year, month, 1);
    const firstDayOfWeek = firstDay.getDay();
    
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);
    const lastDateOfMonth = lastDay.getDate();
    
    // Last day of previous month
    const prevMonthLastDay = new Date(year, month, 0);
    const prevMonthLastDate = prevMonthLastDay.getDate();
    
    const days = [];
    
    // Previous month's trailing days
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonthLastDate - i);
      days.push({ date, isCurrentMonth: false });
    }
    
    // Current month's days
    for (let i = 1; i <= lastDateOfMonth; i++) {
      const date = new Date(year, month, i);
      days.push({ date, isCurrentMonth: true });
    }
    
    // Next month's leading days - only add enough to complete the last row
    const remainingDays = (7 - (days.length % 7)) % 7;
    for (let i = 1; i <= remainingDays; i++) {
      const date = new Date(year, month + 1, i);
      days.push({ date, isCurrentMonth: false });
    }
    
    return days;
  };

  const isDateSelected = (date) => {
    if (!selectedDate || isNaN(selectedDate)) return false;
    return (
      date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear()
    );
  };

  const isToday = (date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const isDateDisabled = (date) => {
    if (!minDate) return false;
    const min = new Date(minDate + 'T00:00:00');
    return date < min;
  };

  const calendarDays = generateCalendarDays();
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <div className="date-picker" ref={containerRef}>
      <div 
        ref={inputRef}
        className={`date-picker-input ${isOpen ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={handleInputClick}
      >
        <Calendar size={18} className="date-picker-icon" />
        <span className={selectedDate ? 'date-picker-value' : 'date-picker-placeholder'}>
          {selectedDate ? formatDisplayDate(selectedDate) : placeholder}
        </span>
        {required && !selectedDate && <span className="required-indicator">*</span>}
      </div>

      {isOpen && (
        <>
          <div className="date-picker-backdrop" onClick={() => setIsOpen(false)} />
          <div 
            className="date-picker-dropdown"
            style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
          >
          {/* Header */}
          <div className="calendar-header">
            <button 
              type="button"
              className="calendar-nav-btn" 
              onClick={handlePrevMonth}
            >
              <ChevronLeft size={18} />
            </button>
            <div className="calendar-month-year">
              {monthNames[displayMonth.getMonth()]} {displayMonth.getFullYear()}
            </div>
            <button 
              type="button"
              className="calendar-nav-btn" 
              onClick={handleNextMonth}
            >
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Weekday headers */}
          <div className="calendar-weekdays">
            <div className="calendar-weekday">Su</div>
            <div className="calendar-weekday">Mo</div>
            <div className="calendar-weekday">Tu</div>
            <div className="calendar-weekday">We</div>
            <div className="calendar-weekday">Th</div>
            <div className="calendar-weekday">Fr</div>
            <div className="calendar-weekday">Sa</div>
          </div>

          {/* Calendar days */}
          <div className="calendar-days">
            {calendarDays.map((day, index) => {
              const disabled = isDateDisabled(day.date);
              const selected = isDateSelected(day.date);
              const today = isToday(day.date);
              
              return (
                <button
                  key={index}
                  type="button"
                  className={`calendar-day 
                    ${!day.isCurrentMonth ? 'other-month' : ''} 
                    ${selected ? 'selected' : ''} 
                    ${today ? 'today' : ''}
                    ${disabled ? 'disabled' : ''}
                  `}
                  onClick={() => !disabled && handleDateSelect(day.date)}
                  disabled={disabled}
                >
                  {day.date.getDate()}
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div className="calendar-footer">
            <button 
              type="button"
              className="calendar-footer-btn" 
              onClick={handleToday}
            >
              Today
            </button>
            {selectedDate && (
              <button 
                type="button"
                className="calendar-footer-btn" 
                onClick={handleClear}
              >
                Clear
              </button>
            )}
          </div>
        </div>
        </>
      )}
    </div>
  );
}

export default DatePicker;
